import React, { forwardRef, useCallback } from 'react';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { ArrowRightCircleOutline } from './arrow-right-circle-outline/index.tsx';

type TransitionHomeStepProps = {
	icon: React.ReactElement;
	title: React.ReactElement | string;
	description: React.ReactElement | string;
	tasks: Array<{
		isCompleted: boolean; // todo: remove isCompleted for optimal tooling
		description: React.ReactElement | string;
	}>;
	onClick: (taskIndex?: number) => void;
};

export const TransitionHomeStep = forwardRef<HTMLElement, TransitionHomeStepProps>(
	({ icon, title, description, tasks, onClick }, ref) => {
		const onKeyDown = useCallback(
			(e: React.KeyboardEvent, taskIndex?: number) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onClick(taskIndex);
				}
			},
			[onClick],
		);

		return (
			<Box
				ref={ref}
				xcss={wrapperBoxStyles}
				padding="space.250"
				onClick={() => onClick()}
				onKeyDown={onKeyDown}
				role="button"
				tabIndex={0}
			>
				<Stack space="space.200">
					<Stack space="space.150">
						<Inline space="space.200">
							{icon}
							<Stack space="space.050">
								<Heading as="h2" size="small">
									{title}
								</Heading>
								{description}
							</Stack>
						</Inline>
					</Stack>
					<Stack space="space.050">
						<Stack>
							{tasks.map((task, index) => (
								<Box
									role="button"
									xcss={taskBoxStyles}
									padding="space.100"
									key={index}
									tabIndex={0}
									onClick={(e: React.MouseEvent<HTMLDivElement>) => {
										e.stopPropagation();
										onClick(index);
									}}
									onKeyDown={(e: React.KeyboardEvent) => {
										e.stopPropagation();
										onKeyDown(e, index);
									}}
								>
									<Inline space="space.050">
										<ArrowRightCircleOutline />

										{task.description}
									</Inline>
								</Box>
							))}
						</Stack>
					</Stack>
				</Stack>
			</Box>
		);
	},
);

const wrapperBoxStyles = xcss({
	boxShadow: 'elevation.shadow.raised',
	borderRadius: 'border.radius.200',
	cursor: 'pointer',
});

const taskBoxStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.raised',
	borderRadius: 'border.radius.100',
	transition: '0.2s ease all',
	':hover': { backgroundColor: 'elevation.surface.overlay.hovered' },
	':focus': { backgroundColor: 'elevation.surface.overlay.hovered' },
});
